import React, { FC, ReactNode } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { AvatarPlaceholderIcon } from '@/components/icons/AvatarPlaceholder'
import Image from 'next/image'

const UploadedAvatarWrapper = styled(Box)`
  overflow: hidden;

  & img {
    border-radius: 6px;
    width: 100%;
    height: 100%;
  }
`

const DefaultAvatarWrapper = styled(Box)`
  color: ${({ theme }) => theme.custom.colors.common.grey2};
  border: 2px solid ${({ theme }) => theme.custom.colors.common.grey};
  background-color: ${({ theme }) => theme.custom.colors.common.grey};
  width: 100%;
  height: 100%;

  & > svg {
    width: inherit;
    height: inherit;
    margin-left: -2px;
    margin-top: -2px;
  }
`

interface WithSizes {
  size?: 'primary' | 'secondary' | 'third' | 'fourth' | 'fifth' | 'sixth' | { width: number; height: number }
}

interface WidthShadow {
  hasShadow: boolean
}

interface UploadedAvatarProps {
  src: string
  hasShadow?: boolean
  style?: React.CSSProperties
}

const AvatarSize = {
  primary: { width: 140, height: 180 },
  secondary: { width: 52, height: 66 },
  third: { width: 100, height: 130 },
  fourth: { width: '100%', height: 'auto' },
  fifth: { width: 155, height: 200 },
  sixth: { width: 75, height: 100 },
}

const AvatarShadows = {
  primary: { boxShadow: '0px 5px 15px -5px rgba(152, 143, 168, 0.55)' },
  secondary: {},
  third: {},
  fourth: {},
  fifth: {},
  sixth: {},
}

const AvatarWrapper: FC<WithSizes & WidthShadow & { children: ReactNode; style?: React.CSSProperties }> = (props) => {
  const { size = 'primary', hasShadow, children, style } = props
  const sizes = typeof size === 'string' ? AvatarSize[size] : size
  const shadow = hasShadow ? AvatarShadows[size as keyof typeof AvatarShadows] : {}
  return (
    <UploadedAvatarWrapper {...sizes} {...shadow} style={style}>
      {children}
    </UploadedAvatarWrapper>
  )
}

export const UploadedAvatar: FC<UploadedAvatarProps & WithSizes & { style?: React.CSSProperties }> = (props) => {
  const { src, size, hasShadow = false, style } = props
  return (
    <AvatarWrapper size={size} hasShadow={hasShadow}>
      <Image
        src={src}
        alt="Uploaded Avatar"
        width={0}
        height={0}
        sizes="100vw"
        style={{ width: '100%', height: 'auto', ...style }}
      />
    </AvatarWrapper>
  )
}

export const DefaultAvatar: FC<WithSizes & { style?: React.CSSProperties }> = (props) => {
  const { size, style } = props
  return (
    <AvatarWrapper hasShadow={false} size={size} style={style}>
      <DefaultAvatarWrapper>
        <AvatarPlaceholderIcon style={{ padding: '0 20%' }} />
      </DefaultAvatarWrapper>
    </AvatarWrapper>
  )
}

export const Avatar: FC<Partial<UploadedAvatarProps> & WithSizes> = (props) => {
  const { src, size, hasShadow, style } = props
  return src ? (
    <UploadedAvatar src={src} size={size} hasShadow={hasShadow} style={style} />
  ) : (
    <DefaultAvatar size={size} style={style} />
  )
}
